import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import RenderIf from '@common/RenderIf';
import { faChevronDown, faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { scrollToNode } from '@helpers/scrollToElement';

import { OptionsPanel } from './blocks/optionsPanel';
import { TSidePanelOption, TSidePanelSelectProps } from './common/types';
import { usePanelPosition } from './common/usePanelPosition';

import css from './sidePanelSelect.module.scss';

/**
 * Выпадающий список, у которого варианты показываются не под полем, а панелью
 * слева от него во всю высоту модалки.
 *
 * Зачем свой вместо `Select` из antd: его попап позиционируется относительно
 * поля и ужимается по остатку места на экране. В карточке характеристик поля
 * идут одно под другим, и у нижних список схлопывался до пары строк — особенно
 * на телефоне с открытой клавиатурой. Панель во всю высоту от положения поля не
 * зависит вовсе.
 *
 * Поведение поля повторяет antd: клик открывает и переводит в режим поиска,
 * выбранное показывается текстом, у необязательных есть крестик очистки.
 */
export const SidePanelSelect: React.FC<TSidePanelSelectProps> = ({
    options,
    value,
    onChange,
    searchValue,
    onSearch,
    open,
    onOpenChange,
    placeholder = 'Выберите вариант',
    disabled,
    hasError,
    allowClear,
    onCreateOption,
    onBlur,
    panelContainer,
    scrollOffset = 8,
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { container, style } = usePanelPosition(triggerRef, open, panelContainer);

    const selectedOption = options.find((option) => option.value === value);
    const hasValue = value !== undefined && value !== null;

    useEffect(() => {
        if (!open) return;

        inputRef.current?.focus();
        // Поле уводим к верхней границе: панель занимает всю высоту, и открытое
        // поле должно быть напротив её начала, а не где-то под ней
        scrollToNode(triggerRef.current, scrollOffset);
    }, [open, scrollOffset]);

    // Клик мимо поля и панели закрывает список: панель лежит в другом узле DOM,
    // поэтому одного onBlur у поля недостаточно
    useEffect(() => {
        if (!open) return undefined;

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if ((target as HTMLElement).closest?.(`.${css.panel}`)) return;

            onOpenChange(false);
            onBlur?.();
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open, onOpenChange, onBlur]);

    const handleSelect = (option: TSidePanelOption) => {
        onChange(option.value, option);
        onSearch('');
        onOpenChange(false);
    };

    const handleClear = (event: React.MouseEvent) => {
        event.stopPropagation();
        onChange(undefined);
        onSearch('');
    };

    const triggerClassName = [
        css.trigger,
        open ? css.triggerOpen : '',
        hasError ? css.triggerError : '',
        disabled ? css.triggerDisabled : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            <div
                ref={triggerRef}
                className={triggerClassName}
                role="combobox"
                aria-expanded={open}
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && onOpenChange(true)}
            >
                <RenderIf condition={open}>
                    <input
                        ref={inputRef}
                        className={css.search}
                        value={searchValue}
                        onChange={(event) => onSearch(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                onOpenChange(false);
                                onBlur?.();
                            }
                            // Enter заводит введённое значение — так же, как
                            // кнопка в панели: на телефоне до неё ещё дотянуться
                            if (event.key === 'Enter' && !options.length && searchValue.trim()) {
                                event.preventDefault();
                                onCreateOption?.(searchValue.trim());
                            }
                        }}
                    />
                </RenderIf>

                <RenderIf condition={!open}>
                    <span className={hasValue ? css.value : css.placeholder}>
                        {selectedOption?.label ?? placeholder}
                    </span>
                </RenderIf>

                <span className={css.suffix}>
                    <RenderIf condition={!!allowClear && hasValue && !open}>
                        <button
                            type="button"
                            className={css.clear}
                            aria-label="Очистить"
                            onClick={handleClear}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </RenderIf>
                    <RenderIf condition={!(allowClear && hasValue && !open)}>
                        <FontAwesomeIcon icon={open ? faMagnifyingGlass : faChevronDown} />
                    </RenderIf>
                </span>
            </div>

            {open &&
                container &&
                createPortal(
                    <OptionsPanel
                        options={options}
                        value={value}
                        searchValue={searchValue}
                        onSelect={handleSelect}
                        onCreateOption={onCreateOption}
                        style={style}
                    />,
                    container
                )}
        </>
    );
};
